import { go } from '../../App';
import { useLoad } from '../../data';
import { BookingTimeline, Loading, MoneyText, Stars, StatusChip, formatWhen } from '../../ui';
import { Booking } from '../../types';

// The supplier's full Booking-detail Record (canon 09 supplier "Booking-detail"
// · Record; §4.2 "Bookings"). One component, reached from the Confirmed
// schedule and from Booking history — the drill target that opens the complete
// story of one job. Supplier-flavoured: the API scopes guestAmount and margin
// out of the supplier's view (DECISION-015), so this shows the gross the
// supplier earns and never a guest price.
//
// Read-only in v1 — a supplier answers and cancels from their phone (the PWA);
// the portal is the desk record. This is the **payment-query home** (canon S6's
// disputed-amount edge): the complete money + timeline picture, and the honest
// route to raise a query (through the hotel / Eva) — no dispute contract exists
// yet (P10-adjacent), so none is invented here.
export function BookingRecord({ id, backTo, tz }: { id: string; backTo: string; tz: string }) {
  const { data: booking, error } = useLoad<Booking>(`/bookings/${id}`);
  const close = () => go(backTo);
  const guests = booking ? booking.guests.adults + (booking.guests.children ?? 0) : 0;

  return (
    <>
      <div className="drawer-veil" onClick={close} />
      <aside className="drawer" role="dialog" aria-label="Booking record">
        <div className="scroll">
          {!booking ? (
            error ? <p className="error-line">{error}</p> : <Loading />
          ) : (
            <>
              <StatusChip status={booking.status} />
              <h1 style={{ marginTop: 12 }}>{booking.productName}</h1>
              <p className="subline">
                {booking.hotelName} · {formatWhen(booking.start)}
              </p>

              <div className="kv">
                <div className="kvrow">
                  <span className="k">When</span>
                  <span className="v">
                    {formatWhen(booking.start)}
                    <span className="hint"> – {booking.end.slice(11, 16)}</span>
                  </span>
                </div>
                <div className="kvrow">
                  <span className="k">Guest</span>
                  <span className="v">
                    {booking.guestName}
                    {booking.guestRoom ? ` · ${booking.guestRoom}` : ''}
                  </span>
                </div>
                <div className="kvrow">
                  <span className="k">Party</span>
                  <span className="v">
                    {booking.guests.adults} adults
                    {booking.guests.children ? ` · ${booking.guests.children} children` : ''} · {guests} total
                  </span>
                </div>
                {booking.notes && (
                  <div className="kvrow">
                    <span className="k">Notes</span>
                    <span className="v">{booking.notes}</span>
                  </div>
                )}
                <div className="kvrow earn">
                  <span className="k">You earn</span>
                  <span className="v">
                    <MoneyText money={booking.supplierAmount} />
                  </span>
                </div>
                {/* The cancellation fee (P10) if the hotel cancelled this
                    confirmed job close to the start — owed to you for the held
                    slot, gross, its own line. */}
                {booking.cancelFee && (
                  <div className="kvrow">
                    <span className="k">Cancellation fee</span>
                    <span className="v">
                      <MoneyText money={booking.cancelFee} />
                    </span>
                  </div>
                )}
                {booking.proposedStart && (
                  <div className="kvrow">
                    <span className="k">You offered</span>
                    <span className="v">{formatWhen(booking.proposedStart)}</span>
                  </div>
                )}
              </div>

              {/* The hotel's review of this job (DECISION-056), if they left one.
                  Read-only — the supplier receives reviews, they don't answer
                  them. The aggregate across all jobs lives on the Reviews screen. */}
              {booking.review && (
                <>
                  <h2 style={{ fontSize: 'var(--text-lg)', color: 'var(--text-secondary)', margin: '4px 0 8px' }}>
                    {booking.hotelName ?? 'The hotel'}'s review
                  </h2>
                  <div className="kv" style={{ marginBottom: 12 }}>
                    <div className="kvrow">
                      <span className="k">Rating</span>
                      <span className="v"><Stars rating={booking.review.rating} /></span>
                    </div>
                    {booking.review.note && (
                      <div className="kvrow">
                        <span className="k">Note</span>
                        <span className="v review-note">“{booking.review.note}”</span>
                      </div>
                    )}
                  </div>
                </>
              )}

              <BookingTimeline timeline={booking.timeline} tz={tz} />

              {/* The payment-query home (canon S6). No dispute contract exists in
                  v1 — the honest route is through the hotel, whom Eva can draft. */}
              <h2 style={{ fontSize: 'var(--text-lg)', color: 'var(--text-secondary)', margin: '16px 0 8px' }}>The amount</h2>
              <p className="hint">
                You earn <MoneyText money={booking.supplierAmount} /> for this job, gross. If something
                looks wrong — a job that changed, a no-show — raise it with{' '}
                {booking.hotelName ?? 'the hotel'}; they hold the guest. Ask Eva to draft the message.
              </p>

              {error && <p className="error-line">{error}</p>}
            </>
          )}
        </div>
        <div className="pinned">
          <button className="btn secondary" onClick={close} style={{ flex: 1 }}>
            Close
          </button>
        </div>
      </aside>
    </>
  );
}
